<template>
    <div v-show="visible" class="w3-modal" style="display: block;">
        <div class="w3-modal-content">
            <header>
                <div class="w3-cell-row">
                    <div class="w3-cell w3-cell-middle">
                        <h2>{{ mode === 'edit' ? $t('Edit domain') : $t('Add domain') }}</h2>
                    </div>
                    <div class="w3-cell w3-right">
                        <a class="close-button" @click="close()">&times;</a>
                    </div>
                </div>
                <hr>
            </header>

            <div class="twi-modal-body twi-form">
                <p v-t="'EditDomain::Intro'">
                </p>

                <div class="w3-cell-row">
                    <div class="w3-cell w3-cell-middle" style="width: 10%;"><strong>{{ $t('Domain') }}</strong>:</div>
                    <div class="w3-cell w3-cell-middle">
                        <select class="w3-input monospace" :disabled="mode === 'edit'" v-model="selectedDomain">
                            <option v-for="domain in domains" :key="domain.recordId" :value="domain">{{ domain.getFqdn() }}</option>
                        </select>
                    </div>
                </div>

                <div class="w3-cell-row">
                    <div class="w3-cell w3-cell-middle" style="width: 10%;"><strong>{{ $t('Token') }}</strong>:</div>
                    <div class="w3-cell">
                        <select class="w3-input monospace" v-model="selectedToken">
                            <option v-for="token in tokens" :key="token.token" :value="token">{{ token.token }}</option>
                        </select>
                    </div>
                </div>

                <h3>{{ $t('Domain update address') }}</h3>
                <p class="twi-text-green twi-underline">{{ domainUpdateAddress }}</p>
                <p>
                    {{ $t('To update this domain, call the URL provided and replace the following placeholders:') }}
                    <ul>
                        <li v-html="$t('Placeholder::IP')"></li>
                        <li v-html="$t('Placeholder::TTL')"></li>
                    </ul>
                </p>
            </div>

            <footer class="w3-cell-row">
                <div class="w3-cell w3-cell-middle" style="width: 50%;">
                    <span v-show="action !== ''">
                        <img src="@/assets/img/spinner-green-32.gif" alt="Loading"> {{ action }}
                    </span>
                </div>
                <div class="w3-cell w3-right-align twi-button-bar">
                    <button v-if="mode === 'edit'" class="w3-button twi-button w3-hover-red" @click="deleteDomain()">{{ $t('Delete domain') }}</button>
                    <button class="w3-button twi-button" @click="saveDomain()">{{ $t('Save domain') }}</button>
                    <button class="w3-button twi-button" @click="close()">{{ $t('Cancel') }}</button>
                </div>
            </footer>
        </div>
    </div>
</template>

<script>
import Api from 'Api'

import Domain from '@/model/Domain'
import TokenRepository from '@/repository/TokenRepository'
import DomainRepository from '@/repository/DomainRepository'

export default {
    data () {
        return {
            visible: false,
            mode: 'create',
            action: '',

            domains: [],
            tokens: [],
            selectedDomain: null,
            selectedToken: null
        }
    },

    computed: {
        domainUpdateAddress: function () {
            if (this.selectedDomain === null || this.selectedToken === null) {
                return this.$i18n.t('Please select a domain and a token to generate an update address.')
            }

            return `${this.$apiBaseUrl}?domain=${this.selectedDomain.getFqdn()}&token=${this.selectedToken.token}` +
                `&ip=<IP>&ttl=<TTL>`
        }
    },

    mounted () {
        this.loadTokens()
    },

    methods: {
        loadTokens () {
            let self = this
            
            self.action = self.$i18n.t('Loading available tokens ...')
            TokenRepository.getUserTokens()
                .then(tokenlist => {
                    self.tokens = tokenlist
                })
                .catch(() => {
                    window.toast({
                        text: self.$i18n.t('Failed to load tokens.'),
                        color: 'red'
                    })
                })
                .finally(() => self.action = '')
        },

        loadUnmappedDomains () {
            let self = this

            self.action = self.$i18n.t('Loading available unmapped domains ...')
            DomainRepository.getUnmappedDomains()
                .then(list => {
                    self.domains = list
                })
                .catch(err => {
                    console.error(err)
                    window.toast({
                        text: self.$i18n.t('Failed to load unmapped domains.'),
                        color: 'red'
                    })
                })
                .finally(() => self.action = '')
        },

        showCreate () {
            this.selectedDomain = null
            this.selectedToken = null
            this.mode = 'create'
            this.visible = true
            this.loadUnmappedDomains()
        },

        showEdit (domain) {
            let self = this
            
            // Find selected token
            for (let token of self.tokens) {
                if (token.token === domain.token) {
                    self.selectedToken = token
                    break
                }
            }

            this.domains = [domain]
            this.selectedDomain = domain
            this.mode = 'edit'
            this.visible = true
        },

        saveDomain () {
            const data = {
                zoneId: this.selectedDomain.zoneId,
                recordId: this.selectedDomain.recordId,
                token: this.selectedToken.token
            }

            if (this.mode === 'create') {
                this.createDomain(data)
            } else {
                this.updateDomain(data)
            }
        },

        createDomain (data) {
            let self = this
            
            self.action = self.$i18n.t('Creating domain ...')
            Api.putDomains(data)
                .then(resp => {
                    if (typeof resp.data !== 'object') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    const domain = Domain.fromObject(resp.data);
                    window.toast({
                        text: self.$i18n.t('The domain {0} has been created.', [domain.getFqdn()]),
                        color: 'green'
                    })
                    self.$emit('created', { data: domain })
                    self.close()
                })
                .catch(err => {
                    if (typeof err.response !== 'object' || typeof err.response.status !== 'number') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    switch (err.response.status) {
                    case 400: {
                        let msg = typeof err.response.data === 'string'
                            ? self.$i18n.t('The domain name {0} can not be used: {1}', [data.domain, err.response.data])
                            : self.$i18n.t('The domain name {0} can not be used.', [data.domain])
                        window.toast({
                            text: msg,
                            color: 'red',
                            timeout: 0
                        })
                        break
                    }
                    case 409:
                        window.toast({
                            text: self.$i18n.t('The domain {0} already exists.', [data.domain]),
                            color: 'red',
                            timeout: 0
                        })
                        break
                    default:
                        window.toast({
                            text: self.$i18n.t('The domain could not be created.'),
                            color: 'red'
                        })
                    }
                })
                .finally(() => self.action = '')
        },

        updateDomain (data) {
            let self = this
            
            self.action = self.$i18n.t('Updating domain ...')
            Api.postDomains(data)
                .then(resp => {
                    if (typeof resp.data !== 'object') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    const domain = Domain.fromObject(resp.data);
                    window.toast({
                        text: self.$i18n.t('The domain {0} has been updated.', [domain.getFqdn()]),
                        color: 'green'
                    })
                    self.$emit('updated', { data: domain })
                    self.close()
                })
                .catch(err => {
                    if (typeof err.response !== 'object' || typeof err.response.status !== 'number') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    switch (err.response.status) {
                    case 404:
                        window.toast({
                            text: self.$i18n.t('The domain {0} does not exist.', [data.domain]),
                            color: 'red',
                            timeout: 0
                        })
                        break
                    default:
                        window.toast({
                            text: self.$i18n.t('The domain could not be updated.'),
                            color: 'red'
                        })
                    }
                })
                .finally(() => self.action = '')
        },

        deleteDomain () {
            let self = this
            if (self.mode !== 'edit') {
                return
            }

            const yn = confirm(self.$i18n.t('Do you really want to permanently remove the domain {0} from dynamic DNS management?', [self.selectedDomain.getFqdn()]))
            if (!yn) {
                return
            }

            const data = {
                zoneId: self.selectedDomain.zoneId,
                recordId: self.selectedDomain.recordId,
                token: self.selectedToken.token
            }

            self.action = self.$i18n.t('Deleting domain ...')
            Api.deleteDomains(data)
                .then(() => {
                    window.toast({
                        text: self.$i18n.t('The domain {0} has been deleted.', [self.selectedDomain.getFqdn()]),
                        color: 'green'
                    })
                    self.$emit('deleted', { data: self.selectedDomain })
                    self.close()
                })
                .catch(err => {
                    if (typeof err.response !== 'object' || typeof err.response.status !== 'number') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    switch (err.response.status) {
                    case 404:
                        window.toast({
                            text: self.$i18n.t('The domain {0} does not exist.', [self.selectedDomain.getFqdn()]),
                            color: 'red',
                            timeout: 0
                        })
                        break
                    default:
                        window.toast({
                            text: self.$i18n.t('The domain could not be deleted.'),
                            color: 'red'
                        })
                    }
                })
                .finally(() => self.action = '')
        },

        close () {
            this.subdomain = ''
            this.selectedDomain = null
            this.selectedToken = null
            this.visible = false
        }
    }
}
</script>

<i18n src="@/lang/common.json"></i18n>
<i18n src="@/lang/domains.json"></i18n>
